/*
 * Copyright (c) Dito Technologies
 */

import React, {ChangeEvent, Dispatch} from "react";
import Button from "ui/kit/buttons/Button";
import {faUpload} from "@fortawesome/free-solid-svg-icons";
import styles from "./UploadWorkspace.module.scss";
import UploadWorkspaceFileList from "ui/feature/root/upload/UploadWorkspaceFileList";
import {
    Action,
    AddFilesAction,
    SetPieceIdAction,
    SetUploadStatusAction,
    State,
    StepStatus,
    UploadProgressAction
} from "ui/feature/root/FormReducer";
import Body from "ui/kit/typography/Body";
import CreateMusicPiece from "mutation/CreateMusicPiece";
import axios from "axios";
import UpdateMusicPage from "mutation/UpdateMusicPage";

interface Props {
    state: State;
    dispatch: Dispatch<Action>
}

const UploadWorkspace: React.FC<Props> = ({state, dispatch}) => {

    const inputFile = React.useRef<HTMLInputElement>(null);

    const openFilePicker = React.useCallback(() => {
        const current = inputFile.current;
        if (current) {
            current.click();
        }
    }, [inputFile]);

    const handleFileChange = React.useCallback((e: ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files) {
            const action: AddFilesAction = {
                type: "add_files",
                files: files
            };
            dispatch(action);
        }
    }, [dispatch]);

    const handleUpload = React.useCallback(async () => {
        const action: SetUploadStatusAction = {
            type: "set_upload_step_status",
            status: StepStatus.IN_PROGRESS
        }
        dispatch(action);
        const fileNames = state.files.map((f) => f.name)
        try {
            const response = await CreateMusicPiece(fileNames);
            const pieceId = response.music_create_piece.piece.id;
            const setPieceIdAction: SetPieceIdAction = {
                type: "set_piece_id",
                pieceId: pieceId
            };
            dispatch(setPieceIdAction);
            const uploadPromises = state.files.map(async (file, idx) => {
                const page = response.music_create_piece.piece.pages[idx];
                await axios.put(page.input_upload_url,
                    file, {
                        onUploadProgress: (progressEvent => {
                            const action: UploadProgressAction = {
                                type: "upload_progress",
                                fileName: file.name,
                                progress: progressEvent.loaded / progressEvent.total
                            }
                            dispatch(action);
                        }),
                        headers: {
                            'Content-Type': 'application/octet-stream'
                        }
                    });
                await UpdateMusicPage(page.id, "UPLOADED");
            });
            await Promise.all(uploadPromises)
            const action: SetUploadStatusAction = {
                type: "set_upload_step_status",
                status: StepStatus.SUCCESS
            }
            dispatch(action);
        } catch (e) {
            console.error(e)
            const action: SetUploadStatusAction = {
                type: "set_upload_step_status",
                status: StepStatus.ERROR
            }
            dispatch(action);
        }

    }, [dispatch, state.files]);

    const uploadTitle = React.useMemo(() => {
        switch (state.uploadStepStatus) {
            case StepStatus.NEEDS_EDIT:
                return "Upload";
            case StepStatus.SUCCESS:
                return "Uploaded";
            case StepStatus.ERROR:
                return "Error, Try Again";
            case StepStatus.IN_PROGRESS:
                return "Uploading...";
        }
    }, [state.uploadStepStatus]);

    const uploadDisabled = React.useMemo(() => {
        switch (state.uploadStepStatus) {
            case StepStatus.NEEDS_EDIT:
                return false;
            case StepStatus.IN_PROGRESS:
                return true;
            case StepStatus.ERROR:
                return false;
            case StepStatus.SUCCESS:
                return false;
        }
    }, [state.uploadStepStatus]);

    return (
        <>
            <input type='file' id='file' multiple ref={inputFile} style={{display: 'none'}}
                   onChange={handleFileChange}/>
            <div className={styles.buttonContainer}>
                <Button
                    title={"Select Files"}
                    icon={faUpload}
                    onClick={openFilePicker}/>
                <Button
                    disabled={uploadDisabled}
                    title={uploadTitle}
                    onClick={handleUpload}/>
            </div>
            <Body className={styles.bodyText}>Select all JPEG, HEIC, PNG or PDF files corresponding to the piece you
                want to convert. Make sure the pages are in order if there is more than one page in your piece.</Body>
            <UploadWorkspaceFileList dispatch={dispatch} state={state}/>
        </>
    )
}

export default UploadWorkspace;